import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Drawer } from "antd";
import {
  BellOutlined,
  CloseOutlined,
  HomeOutlined,
  OrderedListOutlined,
  PlusOutlined,
  ProjectOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthApi } from "../../../services";
import { hasPermission, toFa } from "../../../utils/helpers";
import { COMMANDS } from "../../../components/CommandPalette/commands";
import styles from "./styles.module.css";

/**
 * The four things someone does on a phone, one tap each.
 *
 * Everything used to be behind a hamburger that opened the desktop menu in a
 * drawer, so an employee checking what was assigned to them paid three taps and
 * a drawer animation to reach the screen their day starts on. That drawer is
 * still there for the rest of the panel; it is no longer the only way in.
 *
 * «امروز» is the dashboard, which for anyone without manage_org is already
 * their personal one — their tasks, what is due, what is late.
 */
/**
 * Gated the same way the sidebar is, so the two never offer different answers
 * about what this person has. `permission` hides an item outright;
 * `withoutPermission` is how the two task lists take turns — whoever manages
 * tasks gets «همه تسک‌ها», where their own tasks are a tab, and everyone else
 * gets «تسک‌های من». Projects and notifications are ungated in the sidebar too.
 */
const ITEMS = [
  { key: "/", label: "امروز", icon: <HomeOutlined /> },
  {
    key: "/all-tasks",
    label: "همه تسک‌ها",
    icon: <UnorderedListOutlined />,
    permission: "manage_tasks",
  },
  {
    key: "/my-tasks",
    label: "تسک‌های من",
    icon: <OrderedListOutlined />,
    withoutPermission: "manage_tasks",
  },
  { key: "/projects", label: "پروژه‌ها", icon: <ProjectOutlined /> },
  { key: "/notifications", label: "اعلان‌ها", icon: <BellOutlined /> },
];

/* Quick capture. The same command list the palette uses, so a new action is
 * added in one place and shows up in both — filtered to the ones that create
 * something, since that is what a "+" promises. */
const QUICK_ACTION_KEYS = ["new-project", "new-user", "new-template"];

const BottomNavigation = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [actionsOpen, setActionsOpen] = useState(false);

  const { data: notifications } = useQuery({
    queryKey: ["[DropDown] Notifications"],
    queryFn: () =>
      AuthApi.getNotifications({ params: { per_page: 20 } }).then(
        (result) => result.data.notifications,
      ),
    initialData: [],
    cacheTime: 0,
  });

  const unread = (notifications || []).filter((item) => !item.read_at).length;

  const items = useMemo(
    () =>
      ITEMS.filter(
        (item) =>
          (!item.permission || hasPermission(item.permission)) &&
          (!item.withoutPermission || !hasPermission(item.withoutPermission)),
      ),
    [],
  );

  const actions = useMemo(
    () =>
      COMMANDS.filter(
        (item) =>
          QUICK_ACTION_KEYS.includes(item.key) &&
          (!item.permission || hasPermission(item.permission)),
      ),
    [],
  );

  /* «امروز» is the only exact match; the rest own their whole subtree, so a
   * task open inside a project still highlights پروژه‌ها. */
  const isActive = (key) =>
    key === "/" ? pathname === "/" : pathname.startsWith(key);

  return (
    <>
      <nav className={styles.bar}>
        {items.slice(0, 2).map((item) => (
          <Link
            key={item.key}
            to={item.key}
            className={`${styles.item} ${isActive(item.key) ? styles.itemActive : ""}`}
          >
            <span className={styles.icon}>{item.icon}</span>
            <span className={styles.label}>{item.label}</span>
          </Link>
        ))}

        {actions.length ? (
          <button
            type="button"
            className={styles.fab}
            onClick={() => setActionsOpen(true)}
            aria-label="ساخت مورد جدید"
          >
            <PlusOutlined />
          </button>
        ) : null}

        {items.slice(2).map((item) => (
          <Link
            key={item.key}
            to={item.key}
            className={`${styles.item} ${isActive(item.key) ? styles.itemActive : ""}`}
          >
            <span className={styles.icon}>
              {item.icon}
              {/* Not an antd <Badge>: it anchors the count to the corner of
                  whatever it wraps, and what it wraps here is a 19px glyph, so
                  the corner is still inside the icon and the number landed on
                  top of the bell. Positioned against the icon box instead, it
                  clears the glyph entirely. */}
              {item.key === "/notifications" && unread > 0 ? (
                <span className={styles.badge}>
                  {unread > 99 ? "+۹۹" : toFa(unread)}
                </span>
              ) : null}
            </span>
            <span className={styles.label}>{item.label}</span>
          </Link>
        ))}
      </nav>

      <Drawer
        placement="bottom"
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        height="auto"
        closable={false}
        className={styles.sheet}
      >
        <div className={styles.sheetHead}>
          <span className={styles.sheetTitle}>چی بسازیم؟</span>
          <button
            type="button"
            className={styles.sheetClose}
            onClick={() => setActionsOpen(false)}
            aria-label="بستن"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className={styles.sheetActions}>
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              className={styles.sheetAction}
              onClick={() => {
                setActionsOpen(false);
                navigate(action.to);
              }}
            >
              <span className={styles.sheetActionIcon}>{action.icon}</span>
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </Drawer>
    </>
  );
};

export default BottomNavigation;
